<template>
    <div
        class="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:px-6 lg:px-8"
    >
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
            <a href="/">
                <svg-vue
                    class="mx-auto h-12 w-12"
                    icon="logo-leaf"
                    alt="Nutreance"
                />
            </a>

            <h2 class="mt-6 text-center text-3xl font-bold text-gray-900">
                Create an Account
            </h2>
        </div>

        <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <form
                    ref="form"
                    action="/register"
                    method="POST"
                    @submit.prevent="onSubmit(form)"
                >
                    <input type="hidden" name="_token" :value="csrf" />

                    <div class="mt-3 mb-6">
                        <label
                            for="name"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Name
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                :ref="refs.name"
                                v-model="inputs.name"
                                id="name"
                                name="name"
                                type="text"
                                autocomplete="name"
                                required
                                :class="[
                                    hasError(errors, 'name')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'name') ? 'true' : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'name') ? 'name-error' : ''
                                "
                            />
                            <div
                                v-if="hasError(errors, 'name')"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'name')"
                            class="mt-2 text-sm text-red-600"
                            id="name-error"
                        >
                            {{ error(errors, "name") }}
                        </p>
                    </div>

                    <div class="my-6">
                        <label
                            for="email"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Email
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                :ref="refs.email"
                                v-model="inputs.email"
                                id="email"
                                name="email"
                                type="email"
                                autocomplete="email"
                                required
                                :class="[
                                    hasError(errors, 'email')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'email') ? 'true' : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'email')
                                        ? 'email-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="hasError(errors, 'email')"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'email')"
                            class="mt-2 text-sm text-red-600"
                            id="email-error"
                        >
                            {{ error(errors, "email") }}
                        </p>
                    </div>

                    <div class="my-6">
                        <label
                            for="password"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Password
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                :ref="refs.password"
                                id="password"
                                name="password"
                                type="password"
                                autocomplete="new-password"
                                required
                                :class="[
                                    hasError(errors, 'password')
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="
                                    hasError(errors, 'password')
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-describedby="
                                    hasError(errors, 'password')
                                        ? 'password-error'
                                        : ''
                                "
                            />
                            <div
                                v-if="hasError(errors, 'password')"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="hasError(errors, 'password')"
                            class="mt-2 text-sm text-red-600"
                            id="password-error"
                        >
                            {{ error(errors, "password") }}
                        </p>
                    </div>

                    <div class="my-6">
                        <label
                            for="password_confirmation"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Confirm Password
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                id="password_confirmation"
                                name="password_confirmation"
                                type="password"
                                autocomplete="new-password"
                                required
                                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm focus:ring-nutreance-green focus:border-nutreance-green"
                            />
                        </div>
                    </div>

                    <div class="mt-6 mb-3">
                        <button
                            name="register"
                            type="submit"
                            class="w-full flex justify-center items-center py-3 px-6 border border-transparent rounded-md shadow-sm font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                        >
                            <LoadingIcon
                                v-if="submitted"
                                class="h-5 w-5 mr-2"
                            />
                            <UserAddIcon
                                v-else
                                class="h-5 w-5 mr-2"
                                aria-hidden="true"
                            />
                            Create Your Account
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <p class="mt-4 text-center text-sm text-gray-400">
            Have an account?

            <a
                href="/login"
                class="ml-1 font-medium text-nutreance-green hover:text-nutreance-green-lighter"
            >
                Sign In
            </a>
        </p>
    </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { ExclamationCircleIcon, UserAddIcon } from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";

const props = defineProps<{
    csrf?: string;
    errors?: any;
    old?: any;
}>();

const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const form = ref<any>(null);
const refs = {
    name: ref<any>(null),
    email: ref<any>(null),
    password: ref<any>(null),
};
const inputs = reactive({
    name: oldInput(props.old, "name"),
    email: oldInput(props.old, "email"),
});

onMounted(() => {
    switch (true) {
        case !!hasError(props.errors, "name"):
            return refs.name.value.focus();
        case !!hasError(props.errors, "email"):
            return refs.email.value.focus();
        case !!hasError(props.errors, "password"):
            return refs.password.value.focus();
        default:
            return refs.name.value.focus();
    }
});
</script>
